/**
 * Single source of truth for the marking-HTML sanitization policy, consumed
 * by three layers so they never drift: capture (html-fragment.ts), server
 * write enforcement (sanitize-marking-html.ts — a crafted request can't
 * store what capture wouldn't produce), and render (DOMPurify pass in
 * MarkingDetail.vue, covering records that predate the server enforcement).
 *
 * Must stay environment-agnostic (no DOM APIs) — imported from app/ and server/.
 */

/**
 * Tags removed together with all their content. `button` is reading-view
 * chrome (footnote toggles, panel controls) — its label is never part of the
 * text the user meant to mark.
 */
export const MARKING_REMOVED_TAGS: readonly string[] = [
  'button',
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'svg',
  'math',
  'template',
  'noscript',
]

/**
 * Unwrapped at CAPTURE only (tag dropped, children kept): `mark` is our own
 * wrapper — selecting inside an existing marking must not nest its DOM in the
 * stored HTML. (`a` is handled separately: converted to
 * `<span class="marking-link">` so it can't nest inside the marking card's own
 * link.) Stored fragments legitimately contain `<mark>` again — capture
 * re-adds them — so the server/render layers allow it.
 */
export const MARKING_CAPTURE_UNWRAP_TAGS: readonly string[] = ['mark']

/**
 * Attribute the reading views put on elements that are chrome rather than
 * readable content — action controls and footnote machinery. Everything inside
 * such an element is skipped by both capture paths: the marking text
 * (text-filter.ts) and the stored HTML (html-fragment.ts).
 */
export const MARKING_EXCLUDED_ATTRIBUTE = 'data-marking-exclude'

/**
 * The inverse: content rendered beside a `.body-text` wrapper rather than
 * inside it — journal and article section titles, commentary figures. Without
 * it the containment rule in text-filter.ts reads such text as chrome, and it
 * ends up in the stored fragment but never highlighted in the reading view.
 */
export const MARKING_INCLUDED_ATTRIBUTE = 'data-marking-content'

/**
 * Tags allowed in a STORED fragment. Deliberately absent: interactive
 * elements (a, button, input, …) and raw-text elements (textarea, title,
 * xmp) whose serialization round-trip is an mXSS vector. `img` is allowed
 * but restricted to http(s)/site-relative `src` on every layer.
 */
export const MARKING_STORED_TAGS: readonly string[] = [
  'mark',
  'img',
  'p',
  'div',
  'section',
  'article',
  'span',
  'em',
  'i',
  'strong',
  'b',
  'u',
  's',
  'sub',
  'sup',
  'br',
  'hr',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'caption',
  'colgroup',
  'col',
  'blockquote',
  'pre',
  'code',
  'cite',
  'q',
  'abbr',
  'time',
  'figure',
  'figcaption',
  'label',
  'small',
  'wbr',
  'address',
  'dfn',
  'kbd',
  'samp',
  'var',
  'ins',
  'del',
  'bdi',
  'bdo',
  'ruby',
  'rt',
  'rp',
]

// Classes removed with their children at capture: footnote toggles/bodies
// (collapsed footnote text would leak once `hidden` is stripped), reference
// markers, and the literature "Side N" page-number column. Capture-only
// cosmetics — not a server-side threat.
const REMOVED_CLASS_LITERALS = new Set(['footnote', 'reference', 'page-number'])
const REMOVED_CLASS_PREFIXES: readonly string[] = ['footnote__']

export function hasRemovedMarkingClass(classAttr: string | null | undefined): boolean {
  if (!classAttr) {
    return false
  }

  return classAttr.split(/\s+/).some((cls) => {
    if (!cls) {
      return false
    }

    if (REMOVED_CLASS_LITERALS.has(cls)) {
      return true
    }

    return REMOVED_CLASS_PREFIXES.some(prefix => cls.startsWith(prefix))
  })
}

// Content-level classes the reading views' stylesheets target (BodyText.vue /
// law/Text.vue) — preserved so MarkingDetail can restyle the fragment to
// match the source. Everything else (Tailwind utilities included) is dropped.
export const MARKING_SEMANTIC_CLASS_LITERALS: readonly string[] = [
  'petit',
  'heading',
  'body-text',
  'ded2',
  'ded3',
  'index-head',
  'unindented',
  'legalArticleTitle',
  // Law list markers ('a.', 'b.') — the content has no whitespace after them.
  'data-originalId',
  // Capture-time replacement for <a> — see MARKING_CAPTURE_UNWRAP_TAGS.
  'marking-link',
]

export const MARKING_SEMANTIC_CLASS_PREFIXES: readonly string[] = [
  'jats-',
  'ns-',
  'law-',
  'list-lower-',
  'list-upper-',
]

const SEMANTIC_CLASS_LITERAL_SET = new Set(MARKING_SEMANTIC_CLASS_LITERALS)

export function isSemanticMarkingClass(name: string): boolean {
  if (SEMANTIC_CLASS_LITERAL_SET.has(name)) {
    return true
  }

  return MARKING_SEMANTIC_CLASS_PREFIXES.some(prefix => name.startsWith(prefix))
}

export function filterSemanticMarkingClasses(raw: string): string[] {
  return raw.split(/\s+/).filter(Boolean).filter(isSemanticMarkingClass)
}

/**
 * Allowed `<img>` src: absolute http(s) or site-relative. Everything else
 * (data:, javascript:, blob:, protocol-relative `//host`) is rejected —
 * the fragment ends up in a `v-html` sink.
 */
export function isSafeMarkingImageSrc(src: string | null | undefined): src is string {
  if (!src) {
    return false
  }

  const trimmed = src.trim()

  if (/^https?:\/\//i.test(trimmed)) {
    return true
  }

  return trimmed.startsWith('/') && !trimmed.startsWith('//')
}
